import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface WizardInfo {
  id: string;
  user_id: string;
  user_type: 'investor' | 'entrepreneur';
  completed_at: string;
  profile: {
    email: string;
    full_name: string;
  };
  // Investor specific fields
  investment_interests?: string[];
  investment_amount_range?: string;
  preferred_sectors?: string[];
  // Entrepreneur specific fields
  business_name?: string;
  business_stage?: string;
  funding_needed?: number;
  pitch_deck_url?: string;
}

export const WizardInfoManager = () => {
  const [activeTab, setActiveTab] = useState<'investors' | 'entrepreneurs'>('investors');
  const [wizardData, setWizardData] = useState<WizardInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchWizardInfo();
  }, [activeTab]);

  const fetchWizardInfo = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('wizard_info')
        .select(`
          *,
          profile:profiles(email, full_name)
        `)
        .eq('user_type', activeTab === 'investors' ? 'investor' : 'entrepreneur')
        .order('completed_at', { ascending: false });

      if (error) throw error;
      setWizardData(data || []);
    } catch (error) {
      console.error('Error fetching wizard info:', error);
      toast.error('Failed to load wizard information');
    } finally {
      setIsLoading(false);
    }
  };

  const renderInvestorTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Investment Range</TableHead>
          <TableHead>Sectors</TableHead>
          <TableHead>Completed At</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {wizardData.map((info) => (
          <TableRow key={info.id}>
            <TableCell>{info.profile.full_name}</TableCell>
            <TableCell>{info.profile.email}</TableCell>
            <TableCell>{info.investment_amount_range}</TableCell>
            <TableCell>{info.preferred_sectors?.join(', ')}</TableCell>
            <TableCell>{new Date(info.completed_at).toLocaleDateString()}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const renderEntrepreneurTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Business Name</TableHead>
          <TableHead>Stage</TableHead>
          <TableHead>Funding Needed</TableHead>
          <TableHead>Pitch Deck</TableHead>
          <TableHead>Completed At</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {wizardData.map((info) => (
          <TableRow key={info.id}>
            <TableCell>{info.profile.full_name}</TableCell>
            <TableCell>{info.profile.email}</TableCell>
            <TableCell>{info.business_name}</TableCell>
            <TableCell>{info.business_stage}</TableCell>
            <TableCell>R{info.funding_needed?.toLocaleString()}</TableCell>
            <TableCell>
              {info.pitch_deck_url && (
                <a 
                  href={info.pitch_deck_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  View
                </a>
              )}
            </TableCell>
            <TableCell>{new Date(info.completed_at).toLocaleDateString()}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Onboarding Wizard Information</CardTitle>
        <CardDescription>
          View information collected during the onboarding process for investors and entrepreneurs
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={(value: 'investors' | 'entrepreneurs') => setActiveTab(value)}>
          <TabsList className="mb-4">
            <TabsTrigger value="investors">Investors</TabsTrigger>
            <TabsTrigger value="entrepreneurs">Entrepreneurs</TabsTrigger>
          </TabsList>

          <TabsContent value="investors">
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : (
              renderInvestorTable()
            )}
          </TabsContent>

          <TabsContent value="entrepreneurs">
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : (
              renderEntrepreneurTable()
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}; 